import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { getProductCategoryQuery } from 'graphql-queries';
import {
  GroupType as GraphQLGroupType,
} from 'graphql-query-contracts';
import {
  mapGqlProductCategoryToProductCategory,
  ProductCategory as DomainProductCategory
} from 'domains-billing-web-mappers';

export class GetProductCategoryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GetProductCategoryError';
  }
}

interface UseProductCategoryParams {
  productCategoryId: string;
  groupId?: string;
  skip?: boolean;
}

interface UseProductCategoryResult {
  productCategory: DomainProductCategory | undefined;
  loading: boolean;
  error: GetProductCategoryError | undefined;
}

export const useProductCategory = ({
  productCategoryId,
  groupId,
  skip = false,
}: UseProductCategoryParams): UseProductCategoryResult => {
  const {
    data,
    loading,
    error: queryError,
  } = useQuery(getProductCategoryQuery, {
    variables: {
      getProductCategoryRequest: {
        productCategoryId,
        groupType: GraphQLGroupType.GroupTypeActivateChannel,
        groupId,
      },
    },
    skip: skip || !productCategoryId || !groupId,
    fetchPolicy: 'network-only',
  });

  const processedError = useMemo(() => {
    if (!queryError) return undefined;

    const errorMessage =
      queryError.message || 'Failed to fetch product category';

    return new GetProductCategoryError(errorMessage);
  }, [queryError]);

  const mappedProductCategory = useMemo(() => { 
    if (!data?.getProductCategory?.productCategory) return undefined;

    return mapGqlProductCategoryToProductCategory(data.getProductCategory.productCategory);
  }, [data]);

  return {
    productCategory: mappedProductCategory,
    loading,
    error: processedError,
  };
};
